import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import { all_routes } from "../../router/all_routes";
import { getNocReimbursement } from "../../../core/data/redux/nocReimbursementSlice";
import { getAllNocOnlineCourses, getNocOnlineCourse } from "../../../core/data/redux/nocOnlineCoursesSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import {
  getRequestStatusBadgeClass as getGlobalStatusBadgeClass,
  getRequestStatusLabel as getGlobalStatusLabel,
} from "../../../core/data/constants/requestStatus";
import NOCChatComponent from "../../../components/NOCChat";
import dayjs from "dayjs";

const NOCOnlineCourseReimbursementDetails: React.FC = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();

  // Redux state
  const { currentReimbursement, loading, error } = useSelector(
    (state: RootState) => state.nocReimbursementState
  );
  const { data: nocCoursesList } = useSelector(
    (state: RootState) => state.nocOnlineCoursesState
  );
  const employeesState = useSelector((state: RootState) => state.employeesState);
  const user = employeesState?.data?.data;

  // Local state for direct fetch if needed
  const [data, setData] = useState<any>(null);

  // Parent online course state
  const [parentCourse, setParentCourse] = useState<any>(null);
  const [courseDeptDetails, setCourseDeptDetails] = useState<any>(null);
  const [loadingCourseDetails, setLoadingCourseDetails] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      dispatch(getNocReimbursement({ id: parseInt(id) }));
      dispatch(getMyProfile({}));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (currentReimbursement) {
      setData(currentReimbursement);
    }
  }, [currentReimbursement]);

  // Fetch parent online course if not embedded in reimbursement response
  useEffect(() => {
    let isMounted = true;

    const fetchParentCourseData = async () => {
      const refNo = data?.referenceNumber1 || data?.associatedNocRef;
      if (!refNo) return;

      // If already embedded in reimbursement object, use it directly
      if (data?.online_course || data?.noc_online_course) {
        if (isMounted) setParentCourse(data.online_course || data.noc_online_course);
        return;
      }

      if (isMounted) setLoadingCourseDetails(true);
      try {
        let resolvedCourse: any = null;

        // 1. Try finding in redux store first
        if (Array.isArray(nocCoursesList) && nocCoursesList.length > 0) {
          const match = nocCoursesList.find(
            (c: any) =>
              String(c.referenceNumber).trim() === String(refNo).trim() ||
              String(c.id).trim() === String(refNo).trim()
          );
          if (match) {
            resolvedCourse = match;
          }
        }

        // 2. Try find-one by referenceNumber
        if (!resolvedCourse) {
          try {
            const res = await Api.post(API_URLS.NOC.FIND_ONE_ONLINE_COURSE, {
              referenceNumber: refNo,
            });
            const courseResData = res?.data?.data || res?.data;
            if (
              courseResData &&
              (courseResData.id || courseResData.referenceNumber || courseResData.onlineCourseName)
            ) {
              resolvedCourse = courseResData;
            }
          } catch (err) {
            console.warn("FIND_ONE_ONLINE_COURSE with referenceNumber failed:", err);
          }
        }

        // 3. Try find-all online courses with query: refNo
        if (!resolvedCourse) {
          try {
            const resAll = await Api.post(API_URLS.NOC.FIND_ALL_ONLINE_COURSES, {
              query: refNo,
            });
            const list = resAll?.data?.data?.data || resAll?.data?.data || resAll?.data;
            if (Array.isArray(list) && list.length > 0) {
              const match = list.find(
                (c: any) =>
                  String(c.referenceNumber).trim() === String(refNo).trim() ||
                  String(c.id).trim() === String(refNo).trim()
              );
              if (match) resolvedCourse = match;
              else resolvedCourse = list[0];
            }
          } catch (err) {
            console.warn("FIND_ALL_ONLINE_COURSES with query failed:", err);
          }
        }

        // 4. Try find-all online courses without query (user's online courses list)
        if (!resolvedCourse) {
          try {
            const resAll = await Api.post(API_URLS.NOC.FIND_ALL_ONLINE_COURSES, {
              page: 1,
              limit: 100,
            });
            const list = resAll?.data?.data?.data || resAll?.data?.data || resAll?.data;
            if (Array.isArray(list) && list.length > 0) {
              const match = list.find(
                (c: any) =>
                  String(c.referenceNumber).trim() === String(refNo).trim() ||
                  String(c.id).trim() === String(refNo).trim()
              );
              if (match) resolvedCourse = match;
            }
          } catch (err) {
            console.warn("FIND_ALL_ONLINE_COURSES fallback failed:", err);
          }
        }

        // 5. If we have a resolved course with an ID, fetch its single full details
        if (resolvedCourse && resolvedCourse.id) {
          try {
            const singleRes = await Api.post(API_URLS.NOC.FIND_ONE_ONLINE_COURSE, {
              id: resolvedCourse.id,
            });
            const fullDetail = singleRes?.data?.data || singleRes?.data;
            if (
              fullDetail &&
              (fullDetail.id || fullDetail.course_category || fullDetail.onlineCourseName)
            ) {
              resolvedCourse = fullDetail;
            }
          } catch (err) {
            console.warn("FIND_ONE_ONLINE_COURSE by id failed, continuing with resolvedCourse:", err);
          }
        }

        // 6. Try GET_NOC_DATA if still not resolved
        if (!resolvedCourse) {
          try {
            const masterNocRes = await Api.post(API_URLS.SAP_MASTER_WORKLIST.GET_NOC_DATA, {
              type: "NOC_ONLINECOURSE",
              requestID: refNo,
            });
            const mData = masterNocRes?.data?.data || masterNocRes?.data;
            if (mData && (mData.id || mData.onlineCourseName || mData.course_category)) {
              resolvedCourse = mData;
            }
          } catch (err) {
            console.warn("GET_NOC_DATA for NOC_ONLINECOURSE failed:", err);
          }
        }

        if (isMounted && resolvedCourse) {
          setParentCourse(resolvedCourse);
        }
      } catch (err) {
        console.error("Error resolving parent NOC online course:", err);
      } finally {
        if (isMounted) setLoadingCourseDetails(false);
      }
    };

    if (data?.referenceNumber1 || data?.associatedNocRef || data?.online_course) {
      fetchParentCourseData();
    }

    return () => {
      isMounted = false;
    };
  }, [data, nocCoursesList]);

  // Once parentCourse is available, fetch and match with master course departments if needed
  useEffect(() => {
    let isMounted = true;
    const fetchMasterCourseDepartments = async () => {
      const activeCourse = parentCourse || data?.online_course || data?.noc_online_course;
      if (!activeCourse) return;

      const existingDept =
        activeCourse?.course_category?.course_departments ||
        activeCourse?.course_departments;

      if (
        existingDept &&
        (existingDept.plateform || existingDept.platform) &&
        existingDept.department &&
        existingDept.course
      ) {
        if (isMounted) setCourseDeptDetails(existingDept);
        return;
      }

      const categoryId =
        activeCourse.onlineCourseCategory ||
        activeCourse.course_category?.category_id ||
        activeCourse.course_category?.id;

      try {
        const res = await Api.post(API_URLS.MASTERS.COURSE_DEPARTMENT_LIST, {
          category_id: categoryId || undefined,
        });
        const deptList = res?.data?.data || res?.data || [];
        if (Array.isArray(deptList) && deptList.length > 0) {
          const courseKey = activeCourse.keyForCourse;
          const courseName = activeCourse.onlineCourseName;
          const courseDept = activeCourse.onlineCourseDepartment;

          const matched = deptList.find(
            (item: any) =>
              (courseKey && String(item.course_id) === String(courseKey)) ||
              (courseName && String(item.course).trim().toLowerCase() === String(courseName).trim().toLowerCase()) ||
              (courseName && String(item.course_id) === String(courseName)) ||
              (courseDept && String(item.department).trim().toLowerCase() === String(courseDept).trim().toLowerCase())
          );

          if (isMounted) {
            if (matched) {
              setCourseDeptDetails(matched);
            } else if (existingDept) {
              setCourseDeptDetails(existingDept);
            }
          }
        }
      } catch (err) {
        console.warn("COURSE_DEPARTMENT_LIST fetch error:", err);
        if (isMounted && existingDept) {
          setCourseDeptDetails(existingDept);
        }
      }
    };

    fetchMasterCourseDepartments();
    return () => {
      isMounted = false;
    };
  }, [parentCourse, data]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString || dateString === "-" || dateString === "0000-00-00" || dateString === "00-00-0000") return "-";
    try {
      const d = dayjs(dateString);
      return d.isValid() ? d.format("DD-MM-YYYY") : dateString;
    } catch {
      return dateString;
    }
  };

  const handleDocumentClick = async (doc: any) => {
    if (!doc) return;
    const directUrl = doc.serverURL || doc.url || doc.downloadUrl;
    if (directUrl && (directUrl.startsWith("http://") || directUrl.startsWith("https://"))) {
      window.open(directUrl, "_blank");
      return;
    }
    const key = doc.serverKey || doc.fileName || doc.key;
    if (!key) {
      toast.error("Document key is missing");
      return;
    }
    try {
      const res: any = await Api.post(API_URLS.AWS_BUCKET.GET_PRESIGNED_URL, { key });
      const pUrl = res?.data?.data?.url || res?.data?.url || res?.data?.presignedUrl || res?.data;
      if (pUrl && typeof pUrl === "string" && (pUrl.startsWith("http://") || pUrl.startsWith("https://"))) {
        window.open(pUrl, "_blank");
      } else {
        toast.error("Unable to generate preview link");
      }
    } catch (err) {
      toast.error("Failed to load document");
    }
  };

  if (loading && !data) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading reimbursement details...</p>
        </div>
      </div>
    );
  }

  const reimbursement = data || {};
  const statusStr = reimbursement.requestStatus || reimbursement.status || "T";
  const isClaim =
    reimbursement.claimOrOnlyAdditi === "CL" ||
    reimbursement.claimOrOnlyAdditi === "Reimbursement and Addition" ||
    reimbursement.claimOrOnlyAdditi === "Reimbursement & Addition" ||
    reimbursement.reimbursement_type?.type_id === "CL";

  const applicant = reimbursement.employee || user || {};
  const applicantDetails = applicant.details || applicant || {};
  const employeeName =
    (applicantDetails.firstname
      ? `${applicantDetails.firstname} ${applicantDetails.lastname || applicantDetails.lsastname || ""}`.trim()
      : "") ||
    reimbursement.employeeName ||
    "-";

  const designation =
    applicantDetails.sap_department_designation_wings?.designation_name ||
    applicantDetails.designation_details?.longText ||
    applicantDetails.designation ||
    "-";

  const department =
    applicantDetails.sap_department_designation_wings?.department_name ||
    applicantDetails.department_details?.departmentName ||
    applicantDetails.department ||
    "-";

  const onlineCourse =
    parentCourse ||
    reimbursement.online_course ||
    reimbursement.noc_online_course ||
    {};

  const courseCategory =
    onlineCourse.course_category ||
    reimbursement.course_category ||
    {};

  const courseDept =
    courseDeptDetails ||
    courseCategory.course_departments ||
    onlineCourse.course_departments ||
    {};

  // Field values with robust fallbacks
  const categoryTitle =
    courseCategory.category_title ||
    courseCategory.title ||
    (onlineCourse.onlineCourseCategory === "A1"
      ? "General Course"
      : onlineCourse.onlineCourseCategory === "A2"
      ? "Specialized Course"
      : onlineCourse.onlineCourseCategory) ||
    "-";

  const departmentName =
    courseDept.department ||
    onlineCourse.onlineCourseDepartment ||
    onlineCourse.department ||
    "-";

  const courseName =
    onlineCourse.onlineCourseName ||
    courseDept.course ||
    onlineCourse.courseName ||
    "-";

  const platformName =
    courseDept.plateform ||
    courseDept.platform ||
    onlineCourse.plateform ||
    onlineCourse.platform ||
    "-";

  const rawDuration =
    courseDept.duration ||
    onlineCourse.durationOfOnlineCourse ||
    onlineCourse.duration ||
    reimbursement.durationOfOnlineCourse;
  const durationDisplay = rawDuration
    ? String(rawDuration).toLowerCase().includes("week")
      ? String(rawDuration).trim()
      : `${String(rawDuration).trim()} Weeks`
    : "-";

  const courseFeeVal =
    courseDept.course_fee ||
    courseDept.courseFee ||
    onlineCourse.courseFee ||
    onlineCourse.course_fee ||
    "0";
  const courseFeeDisplay =
    courseFeeVal && !isNaN(Number(courseFeeVal))
      ? `₹ ${Number(courseFeeVal).toLocaleString("en-IN")}`
      : courseFeeVal
      ? `₹ ${courseFeeVal}`
      : "₹ 0";

  const contributorName =
    courseDept.contributor ||
    courseDept.contributors ||
    onlineCourse.contributor ||
    "-";

  // History & remarks list
  const approvalHistory = Array.isArray(reimbursement.remarks_data) && reimbursement.remarks_data.length > 0
    ? reimbursement.remarks_data
    : Array.isArray(reimbursement.work_history) && reimbursement.work_history.length > 0
    ? reimbursement.work_history
    : [];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.onlineCourse}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <h3 className="page-title mb-0">
                Online Course Reimbursement - {reimbursement.referenceNumber || "-"}
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.onlineCourse}>Online Course</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Reimbursement Details
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Chat Component */}
        {reimbursement.id && (
          <NOCChatComponent
            nocRequestId={reimbursement.id}
            nocType="ONLINE-COURSE-REIMBURSEMENT"
            employeeName={employeeName}
            employeeId={reimbursement.empID || reimbursement.empId}
            employeeImage={applicantDetails?.image}
            approverId={reimbursement.approverUserID || reimbursement.approverId}
          />
        )}

        {/* 1. Header / Application Details Card */}
        <div className="card mt-4 border-0 shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h4 className="mb-0 fw-bold">Application Details</h4>
              <p className="text-muted mb-0 mt-1 small">
                Reference Number: {reimbursement.referenceNumber || "-"}
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary fs-13">
                {isClaim ? "Reimbursement & Addition" : "Only Addition"}
              </span>
              <span className={`badge ${getGlobalStatusBadgeClass(statusStr)} fs-13`}>
                {getGlobalStatusLabel(statusStr)}
              </span>
            </div>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Ref No.</small>
                  <div className="fw-bold text-dark fs-15">{reimbursement.referenceNumber || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Associated NOC Ref No.</small>
                  <div className="fw-bold text-primary fs-15">
                    {parentCourse?.id ? (
                      <Link
                        to={`${routes.noc.onlineCourse}/details/${parentCourse.id}`}
                        className="text-primary text-decoration-underline"
                        title="View Associated NOC Online Course"
                      >
                        {reimbursement.referenceNumber1}
                      </Link>
                    ) : (
                      reimbursement.referenceNumber1 || "-"
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Currently With</small>
                  <div className="fw-bold text-dark fs-15">
                    {reimbursement.approverName
                      ? `${reimbursement.approverName} (${formatEmpIdForDisplay(reimbursement.approverUserID || "")})`
                      : formatEmpIdForDisplay(reimbursement.approverUserID || "-")}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Creation Date</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(reimbursement.requestCreationDate || reimbursement.created_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Employee Details Card */}
        <div className="card mt-4 border-0 shadow-sm">
          <div className="card-header bg-light d-flex align-items-center">
            <i className="ti ti-user me-2 text-primary fs-18"></i>
            <h5 className="card-title mb-0">Employee Details</h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Name</small>
                  <div className="fw-bold text-dark fs-15">{employeeName}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Employee ID</small>
                  <div className="fw-bold text-primary fs-15">
                    {formatEmpIdForDisplay(reimbursement.empID || reimbursement.empId || applicantDetails.empId || "")}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Designation</small>
                  <div className="fw-bold text-dark fs-15">{designation}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Date of Joining in DMRC</small>
                  <div className="fw-bold text-dark fs-15">{formatDate(applicantDetails.date_of_joining)}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Employment Status</small>
                  <div className="fw-bold text-dark fs-15">{applicantDetails.employmentType || "Direct recruit"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Department</small>
                  <div className="fw-bold text-dark fs-15">{department}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Active Phone Number</small>
                  <div className="fw-bold text-dark fs-15">{reimbursement.contactNumber || applicantDetails.phone || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Email ID</small>
                  <div className="fw-bold text-dark fs-15">{reimbursement.email || applicantDetails.email || "-"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Course Details as per HR Compendium */}
        <div className="card mt-4 border-0 shadow-sm">
          <div className="card-header bg-light d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className="ti ti-book me-2 text-primary fs-18"></i>
              <h5 className="card-title mb-0">Course Details as per HR Compendium</h5>
            </div>
            {loadingCourseDetails && (
              <div className="d-flex align-items-center text-muted small">
                <span className="spinner-border spinner-border-sm me-2 text-primary" role="status"></span>
                <span>Fetching course details...</span>
              </div>
            )}
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Category</small>
                  <div className="fw-bold text-dark fs-15">{categoryTitle}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Department</small>
                  <div className="fw-bold text-dark fs-15">{departmentName}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Course Name</small>
                  <div className="fw-bold text-dark fs-15">{courseName}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Platform</small>
                  <div className="fw-bold text-dark fs-15">{platformName}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Duration</small>
                  <div className="fw-bold text-dark fs-15">{durationDisplay}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Online Course Fee</small>
                  <div className="fw-bold text-success fs-15">{courseFeeDisplay}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Contributors</small>
                  <div className="fw-bold text-dark fs-15">{contributorName}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Reimbursement & Addition Details */}
        <div className="card mt-4 border-0 shadow-sm">
          <div className="card-header bg-light d-flex align-items-center">
            <i className="ti ti-file-dollar me-2 text-primary fs-18"></i>
            <h5 className="card-title mb-0">Reimbursement & Addition Details</h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Request Type</small>
                  <div className="fw-bold text-dark fs-15">
                    {isClaim ? "Reimbursement and Addition of record" : "Only Addition of record in System"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Block Year Ending On</small>
                  <div className="fw-bold text-dark fs-15">{formatDate(reimbursement.endOfBlockDate) || "-"}</div>
                </div>
              </div>
              {isClaim && (
                <>
                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block text-uppercase fw-bold mb-1">Amount Remaining for Block Year</small>
                      <div className="fw-bold text-primary fs-15">₹ {reimbursement.eligibilityAmount || "18,000"} /-</div>
                    </div>
                  </div>
                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block text-uppercase fw-bold mb-1">Requested Amount</small>
                      <div className="fw-bold text-success fs-15">₹ {reimbursement.requestAmount || "0.00"} /-</div>
                    </div>
                  </div>
                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block text-uppercase fw-bold mb-1">Approved Amount</small>
                      <div className="fw-bold text-success fs-15">
                        ₹ {reimbursement.approvedAmount || "0.00"} /-
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Date of Joining Course</small>
                  <div className="fw-bold text-dark fs-15">{formatDate(reimbursement.dOJOnlineCourse)}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Date of Completion of Course</small>
                  <div className="fw-bold text-dark fs-15">{formatDate(reimbursement.courseCompletionDate)}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Duration of Course</small>
                  <div className="fw-bold text-dark fs-15">
                    {reimbursement.durationOfOnlineCourse ? `${reimbursement.durationOfOnlineCourse} Weeks` : "-"}
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="p-3 bg-light rounded border">
                  <small className="text-muted d-block text-uppercase fw-bold mb-1">Employee Remarks</small>
                  <div className="text-dark">{reimbursement.remarks || "No remarks provided"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Documents / Attachments Card */}
        <div className="card mt-4 border-0 shadow-sm">
          <div className="card-header bg-light d-flex align-items-center">
            <i className="ti ti-files me-2 text-primary fs-18"></i>
            <h5 className="card-title mb-0">
              Attached Documents ({Array.isArray(reimbursement.documents) ? reimbursement.documents.length : 0})
            </h5>
          </div>
          <div className="card-body">
            <div className="alert alert-danger bg-danger-transparent border-danger mb-3 p-3">
              <h6 className="text-danger mb-2">
                <i className="ti ti-info-circle me-2"></i> Mandatory Attachments
              </h6>
              <ul className="mb-0 ps-3 small text-muted">
                {isClaim && (
                  <>
                    <li>• Fee Receipt is mandatory for reimbursement claims.</li>
                    <li>• Proof of payment in INR (if fee was paid in foreign currency).</li>
                  </>
                )}
                <li>• Course Completion Certificate is mandatory.</li>
              </ul>
            </div>

            {Array.isArray(reimbursement.documents) && reimbursement.documents.length > 0 ? (
              <div className="table-responsive border rounded">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Document Type</th>
                      <th>File Name</th>
                      <th>Uploaded Date</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reimbursement.documents.map((doc: any, idx: number) => {
                      const typeLabel =
                        doc.typeOfAttachedDoc === "01"
                          ? "Fee Receipt"
                          : doc.typeOfAttachedDoc === "02"
                          ? "Proof of Payment in INR"
                          : doc.typeOfAttachedDoc === "03"
                          ? "Course Completion Certificate"
                          : doc.documentType || "Attachment";
                      return (
                        <tr key={idx}>
                          <td>
                            <span className="badge bg-primary-subtle text-primary border">{typeLabel}</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <i className="ti ti-file-text fs-18 text-primary me-2"></i>
                              <span className="fw-medium">
                                {doc.originalName || (doc.fileName ? doc.fileName.split("/").pop() : "Document")}
                              </span>
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">{formatDate(doc.created_at || reimbursement.created_at)}</small>
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleDocumentClick(doc)}
                            >
                              <i className="ti ti-eye me-1"></i>
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted text-center py-3">No documents attached</div>
            )}
          </div>
        </div>

        {/* 6. Approval History & Previous Remarks */}
        {approvalHistory.length > 0 && (
          <div className="card mt-4 border-0 shadow-sm">
            <div className="card-header bg-light d-flex align-items-center">
              <i className="ti ti-history me-2 text-primary fs-18"></i>
              <h5 className="card-title mb-0">Approval History & Remarks</h5>
            </div>
            <div className="card-body">
              <div className="d-flex flex-column gap-3">
                {approvalHistory.map((item: any, idx: number) => (
                  <div key={idx} className="p-3 bg-white rounded border border-start-4 border-start-primary shadow-xs">
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                      <div className="d-flex align-items-center gap-2">
                        <div className="avatar avatar-sm bg-primary-transparent text-primary rounded-circle fw-bold">
                          <i className="ti ti-user-check fs-16"></i>
                        </div>
                        <div>
                          <h6 className="mb-0 text-dark fw-bold">{item.approverName || item.name || "Approver"}</h6>
                          <small className="text-muted">
                            ID: {formatEmpIdForDisplay(item.approverUserID || item.empID || "")}
                          </small>
                        </div>
                      </div>
                      <span className="badge bg-light text-muted border">
                        <i className="ti ti-calendar me-1"></i>
                        {formatDate(item.createdAt || item.timeStamp || item.created_at)}
                      </span>
                    </div>
                    {item.remarks && (
                      <p className="mb-0 text-dark small mt-2 pt-2 border-top">
                        <i className="ti ti-quote text-muted me-1"></i>
                        {item.remarks}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 7. Notes */}
        <div className="card mt-4 border-info">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0 text-dark">
              <i className="ti ti-info-circle me-2 text-info"></i>
              Notes
            </h5>
          </div>
          <div className="card-body">
            <ul className="mb-0 text-dark small ps-3">
              <li>• Reimbursement request should be applied within 6 months of completion of the course.</li>
            </ul>
          </div>
        </div>

        {/* Read-only Notice banner */}
        <div className="alert alert-success-light d-flex align-items-center mt-4 bg-success-transparent text-success border border-success-subtle p-3 rounded">
          <i className="ti ti-info-circle me-2 fs-18"></i>
          <span>This is a read-only view of your submitted Online Course Reimbursement application.</span>
        </div>
      </div>
    </div>
  );
};

export default NOCOnlineCourseReimbursementDetails;
